import React, { useEffect, useState } from 'react';
import { db } from '../services/firebase';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Share2, BookOpen } from 'lucide-react';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  tags: string[];
  image?: string;
}

function calculateReadingTime(content: string) {
  const wordsPerMinute = 200;
  const wordCount = content.replace(/<[^>]+>/g, '').split(/\s+/).length;
  return Math.ceil(wordCount / wordsPerMinute);
}

// Table of Contents
const TableOfContents = ({ content }: { content: string }) => {
  const [headings, setHeadings] = useState<{ id: string; text: string; level: number }[]>([]);

  useEffect(() => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const headingElements = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const headingData = Array.from(headingElements).map((heading, index) => {
      const id = `heading-${index}`;
      heading.id = id;
      return {
        id,
        text: heading.textContent || '',
        level: parseInt(heading.tagName.charAt(1)),
      };
    });
    setHeadings(headingData);
  }, [content]);

  if (headings.length === 0) return null;

  return (
    <div className="toc-container">
      <h3 className="toc-title">
        <BookOpen className="w-4 h-4" />
        Table of Contents
      </h3>
      <nav className="toc-nav">
        {headings.map((heading) => (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            className={`toc-item ${heading.level === 1
                ? 'toc-item-h1'
                : heading.level === 2
                  ? 'toc-item-h2'
                  : 'toc-item-h3'
              }`}
          >
            {heading.text}
          </a>
        ))}
      </nav>
    </div>
  );
};

const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const selectedBlog = id ? posts.find((p) => p.id === id) : null;

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'blogs'), orderBy('date', 'desc'));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() })) as BlogPost[];
        setPosts(data);
      } catch (err) {
        setError('Failed to load blog posts.');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Fetch single post if not in list
  useEffect(() => {
    const fetchSinglePost = async () => {
      if (id && !selectedBlog && !loading) {
        setLoading(true);
        try {
          const docRef = doc(db, 'blogs', id);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const postData = { id: docSnap.id, ...docSnap.data() } as BlogPost;
            setPosts((prev) => [postData, ...prev]);
          } else {
            setError('Blog post not found.');
          }
        } catch (err) {
          setError('Failed to load blog post.');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchSinglePost();
  }, [id, selectedBlog, loading]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl md:border-x px-4 py-24">
        <p className="text-sm text-muted-foreground text-center">{error}</p>
      </div>
    );
  }

  if (!selectedBlog) {
    // Redirect to articles page if no specific post
    navigate('/articles');
    return null;
  }

  return (
    <div className="mx-auto max-w-3xl md:border-x">
      <div className="px-4 py-12">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/articles')}
          className="mb-8 text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Articles
        </motion.button>

        <motion.article
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <header className="mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight text-foreground">
              {selectedBlog.title}
            </h1>

            <p className="text-base text-muted-foreground mb-6 leading-relaxed">
              {selectedBlog.excerpt}
            </p>

            <div className="flex items-center justify-between py-4 screen-line-before screen-line-after">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{selectedBlog.author}</span>
                <span>·</span>
                <span>
                  {new Date(selectedBlog.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                <span>·</span>
                <span>{calculateReadingTime(selectedBlog.content)} min read</span>
              </div>

              <button
                onClick={() => {
                  const shareData = {
                    title: selectedBlog.title,
                    url: window.location.href,
                    text: selectedBlog.excerpt,
                  };
                  if (navigator.share) {
                    navigator.share(shareData);
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </header>

          {/* Hero Image */}
          {selectedBlog.image && (
            <img
              src={selectedBlog.image}
              alt={selectedBlog.title}
              className="w-full h-auto max-h-96 object-cover rounded-lg mb-8"
            />
          )}

          {/* Table of Contents */}
          <TableOfContents content={selectedBlog.content} />

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <div dangerouslySetInnerHTML={{ __html: selectedBlog.content }} />
          </div>

          {/* Tags */}
          {selectedBlog.tags && selectedBlog.tags.length > 0 && (
            <div className="mt-12 pt-8 screen-line-before">
              <div className="flex flex-wrap gap-2">
                {selectedBlog.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-xs font-mono text-muted-foreground border border-border rounded"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.article>
      </div>
    </div>
  );
};

export default BlogPage;